import {Button, Col, Row, Spin} from "antd";
import {
    ComicRawIcon,
    ComicSlabbedIcon,
    DiamondIcon,
    DollarIcon,
    FlameIcon,
    KeyIcon,
    StorageBoxOutlineIcon,
    WishlistIcon
} from "@components/icons/Icons";

import React, {useEffect, useMemo, useState} from "react";
import {Pie} from '@ant-design/plots';
import {formatDateTiny, formatGrade, formatMoney, formatNumber} from "@lib/format";
import ColorPill from "@components/color-pill";
import {ColorBlock} from "@components/color-block";
import ColorBlockIcon from "@components/color-block/ColorBlockIcon";
import {useGetCollectionOverviewQuery, useRefreshCollectionFmvMutation} from "@mwServices/User";
import {Contain} from "@components/layout";
import {SyncOutlined} from "@ant-design/icons";
import moment from "moment";
import {showResponse} from "@services/Api";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {BoxesAddDrawer} from "@components/boxes-add-drawer";
import {useNavigate} from "react-router-dom";
import {FmvPrice} from "@components/fmv-price";
import {showInfoModal} from "@lib/showModal";
import {useWindowSize} from "@components/window-size-provider";

const dummyStats = require('./dummyStats.json');

const MyCollectionOverview = ({ goToCollectionWithFilters }) => {
    const navigate = useNavigate();
    const windowDimensions = useWindowSize();

    const subscriptionType = useSubscriptionType();
    const showOverview = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const allowBoxes = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const [addBox, setAddBox] = useState(false);
    const overviewQuery = useGetCollectionOverviewQuery();
    const [postRefreshFmv, refreshFmvResponse] = useRefreshCollectionFmvMutation();
    const overviewData = useMemo(() => {
        if (showOverview && overviewQuery) return overviewQuery.data;
        return dummyStats;
    }, [showOverview, overviewQuery]);

    const fmvUpdated = useMemo(() => overviewData && overviewData.info.fmv_updated ? moment(overviewData.info.fmv_updated).toDate() : null, [overviewData]);

    useEffect(() => {
        showResponse(refreshFmvResponse, 'Collection fmv calculating, please wait.', 'Sorry, there was a problem refreshing the collection FMV.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshFmvResponse]);

    const hasTopBox = useMemo(() => overviewData && !!overviewData.info.top_box, [overviewData])

    const onBoxesInfoClick = () => {
        showInfoModal({
            title: "Virtual Boxes",
            content: (
                <>
                    <p>
                        Creating a Virtual Box allows you to group and explore your collection in new ways.
                        For example, create a Virtual Box for "Things to Sell" or "My Favorite Cover Artist."
                        Boxes allow you quick access to a subset of your comics while providing useful value summaries and much more.
                    </p>
                    <p>
                        How to Start:<br />
                        Step 1 - Navigate to the Virtual Boxes tab below and create a box.<br />
                        Step 2 - Add individual comics by selecting a comic or add multiple comics at once via the EDIT button.<br />
                    </p>
                    <p>NOTE: You are able to create a total of 20 boxes.</p>
                </>
            ),
            onOk() { },
        });
    };

    const [pieData, setPieData] = useState([
        { value: 0, color: "Raw" },
        { value: 0, color: "Slabbed" }
    ]);

    useEffect(() => {
        if (overviewData) {
            const ungraded = Number(overviewData.info.ungraded);
            const graded = Number(overviewData.info.graded);

            if (pieData) {
                if (pieData[0].value !== ungraded || pieData[1].value !== graded) {
                    setPieData([
                        { value: ungraded, color: "Raw" },
                        { value: graded, color: "Slabbed" }
                    ]);
                }
            }

            if(overviewData.info.calculation_processing){
                setTimeout(overviewQuery.refetch, 5000);
            }
        }
    }, [overviewData]);

    const showVirtualBox = useMemo(() => {
        return windowDimensions.over(1100);
    }, [windowDimensions]);

    const showHighestValued = useMemo(() => {
        return windowDimensions.over(600);
    }, [windowDimensions]);

    const showBiggerIcons = useMemo(() => {
        return windowDimensions.over(900);
    }, [windowDimensions]);

    return (
        <Spin spinning={overviewQuery.isLoading}>
            <Contain>
                <Row gutter={showVirtualBox ? 15 : 5}
                    className={'overview-row ' + (showBiggerIcons ? '' : 'small-screen')}
                >
                    <Col span={showVirtualBox ? 8 : showHighestValued ? 10 : 16}>
                        <Spin spinning={refreshFmvResponse.isLoading}>
                            <ColorBlock
                                backgroundColor='#2f404a'
                                color="white"
                                onInfoClick={() => {
                                    showInfoModal({
                                        title: "Estimated Collection Value",
                                        content: (
                                            <>
                                                <p>
                                                    Everyday <b>COVR</b>PRICE gathers sales from major online marketplaces to generate the Fair Market Values (FMVs)
                                                    for comics in your collection. We update these values multiple times throughout the day. When fresh sales
                                                    are added the UPDATE button will appear, giving you option to re-calculate your Estimated Collection Value.
                                                    Please be patient as this calculation process may take a few seconds depending on the size of your collection.
                                                </p>
                                                <p>
                                                    <b>NOTE:</b>
                                                    If you add a comic or make a change to your collection after you've calculated your Collection's Value,
                                                    don't worry, you don't have to wait for the UPDATE button be become available again. We'll automatically
                                                    update your total collection value based on any individual changes you make.
                                                </p>
                                            </>
                                        ),
                                        onOk() { },
                                    });
                                }}
                                leftChild={<DollarIcon className={"blue-grey " + (showBiggerIcons ? ' whiteCircleIcon' : ' whiteCircleIcon50px') } />}
                                rightTitle="Est. Collection Value"
                                rightChild={
                                    <Row className={"flex-row flex-vertical-center flex-space-between width-100 mar-right-20"}>
                                        <FmvPrice
                                            value={overviewData ? overviewData.info.fmv : 0}
                                            stale={overviewData && overviewData.info.fmv_is_stale}
                                            staleInfo="FMV data is stale, please update."
                                        />
                                        {(overviewData && overviewData.info.fmv_can_update && !overviewData.info.calculation_processing) && (
                                            <Button
                                                size={"small"}
                                                className={"whiteButton " + (showBiggerIcons ? ' smallButton' : ' tinyButton')}
                                                onClick={async () => {
                                                    await postRefreshFmv({});
                                                }}
                                            >
                                                Update
                                                {!windowDimensions.isSmallScreen && (<SyncOutlined />)}
                                            </Button>
                                        )}
                                    </Row>
                                }
                                rightSubtext={
                                    <span>
                                        <Row>
                                            <span className='font-10 text-grey'>
                                                {(overviewData && overviewData.info.fmv_can_update) ? (
                                                    <>
                                                        {fmvUpdated ? (
                                                            <>Updated: {formatDateTiny(fmvUpdated)}</>
                                                        ) : (
                                                            <>Update Your Collection</>
                                                        )}
                                                    </>
                                                ) : overviewData && overviewData.info.calculation_processing ? (
                                                    <>Processing, please wait...</>
                                                ) : (
                                                    <>Your Collection Is Up-To-Date</>
                                                )}
                                            </span>
                                        </Row>
                                    </span>
                                }
                                className='mar-bot-15'
                            />
                        </Spin>

                        <ColorBlock
                            backgroundColor='#ffffff'
                            color="#45535e"
                            leftChild={<div className={(showBiggerIcons ? ' square-70' : ' square-50')}><Pie
                                data={pieData}
                                angleField='value'
                                colorField='color'
                                radius={1}
                                innerRadius={0.5}
                                autoFit={true}
                                color={["#4f94a3", "#6eccb4"]}
                                startAngle={3}
                                endAngle={2.99}
                                label={{
                                    content: () => ``,
                                }}
                                statistic={{
                                    title: false,
                                    content: false,
                                }}
                                legend={false}
                            /></div>}
                            rightTitle={"Items In Collection"}
                            rightChild={`${overviewData ? formatNumber(overviewData.info.items) : 0}`}
                            rightSubtext={(
                                <div className={'overview-pie-bottom ' + (showBiggerIcons ? '' : ' small-screen')}>
                                    <ColorPill
                                        width={showBiggerIcons ? 20 : 15}
                                        color={"#4f94a3"}
                                        style={{ marginRight: showBiggerIcons ? '5px' : '2px', marginTop: '2px' }}
                                    />
                                    <div >Raw&nbsp;|&nbsp;{overviewData ? formatNumber(overviewData.info.ungraded) : 0}</div>
                                    <span className={showBiggerIcons ? 'width-20px' : 'width-5px'} />
                                    <ColorPill
                                        width={showBiggerIcons ? 20 : 15}
                                        color={"#6eccb4"}
                                        style={{ marginRight: showBiggerIcons ? '5px' : '2px', marginTop: '2px' }}
                                    />
                                    <div>Slabbed&nbsp;|&nbsp;{overviewData ? formatNumber(overviewData.info.graded) : 0}</div>
                                </div>
                            )}
                        />
                    </Col>

                    {showVirtualBox && (
                        <Col span={4}>
                            <ColorBlock
                                backgroundColor="#f8da78"
                                color="#9b6815"
                                type="vertical"
                                leftChild={<StorageBoxOutlineIcon className="whiteCircleIconSmall storage-icon" />}
                                rightTitle={
                                    hasTopBox ? (
                                        <div className="highest-valued-title">
                                            Highest Valued<br />
                                            Virtual Box
                                        </div>
                                    ) : (
                                        <>
                                            Create and manage<br />
                                            subsections of your collection<br />
                                            with our Virtual Boxes.
                                        </>
                                    )}
                                rightChild={
                                    hasTopBox ? (
                                        <>
                                            <div className="box-title">{overviewData.info.top_box.name}</div>
                                            <div className="box-value">{formatMoney(overviewData.info.top_box.fmv)}</div>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                size={"small"}
                                                className="whiteButton smallButton"
                                                onClick={() => {
                                                    if (allowBoxes) {
                                                        setAddBox(true);
                                                    } else {
                                                        navigate(`/my-collection/?view=boxes`);
                                                    }
                                                }}
                                            >

                                                Create a VB
                                            </Button>
                                            <Button
                                                className="button-link"
                                                style={{ color: '#9b6815' }}
                                                type="link"
                                                onClick={onBoxesInfoClick}
                                            >Learn More</Button>
                                        </>
                                    )}
                                onInfoClick={onBoxesInfoClick}
                            />
                        </Col>
                    )}

                    {showHighestValued && (
                        <Col span={showVirtualBox ? 7 : 9}>
                            <a onClick={() => {
                                if (overviewData) {
                                    goToCollectionWithFilters({itemInCollectionId: overviewData.info.top_value_graded.id});
                                }
                            }}>
                                <ColorBlock
                                    backgroundColor='#308b86'
                                    color="white"
                                    type="half"
                                    leftChild={<div className="flex pad-left-20">
                                        <ComicSlabbedIcon className={showBiggerIcons ? "icon50" : "icon30"} />
                                        <div className="pad-left-10 text-white">Highest<br />Slabbed Comic</div>
                                    </div>}
                                    rightChild={overviewData && overviewData.info.top_value_graded ? (<div>
                                        <div className={showBiggerIcons ? 'block-font-24' : 'block-font-16'}>{formatMoney(overviewData.info.top_value_graded.estval)}</div>
                                        <div className={showBiggerIcons ? 'block-font-12-light' : 'block-font-10-light'} >{overviewData.info.top_value_graded.comic_title}</div>
                                        <div className={showBiggerIcons ? 'block-font-14' : 'block-font-12'}>{overviewData.info.top_value_graded.graded_by} {formatGrade(overviewData.info.top_value_graded.grade)}</div>
                                    </div>) : "none"}
                                    className="mar-bot-15 cursor-pointer"
                                />
                            </a>

                            <a onClick={() => {
                                if (overviewData) {
                                    goToCollectionWithFilters({itemInCollectionId: overviewData.info.top_value_raw.id});
                                }
                            }}>
                                <ColorBlock
                                    backgroundColor='#396975'
                                    color="white"
                                    type="half"
                                    leftChild={<div className="flex pad-left-20">
                                        <ComicRawIcon className={showBiggerIcons ? "icon50" : "icon30"} />
                                        <div className="pad-left-10 text-white">Highest<br />Raw Comic</div>
                                    </div>}
                                    rightChild={overviewData && overviewData.info.top_value_raw ? (<div>
                                        <div className={showBiggerIcons ? 'block-font-24' : 'block-font-16'}>{formatMoney(overviewData.info.top_value_raw.estval)}</div>
                                        <div className={showBiggerIcons ? 'block-font-12-light' : 'block-font-10-light'}>{overviewData.info.top_value_raw.comic_title}</div>
                                        <div className={showBiggerIcons ? 'block-font-14' : 'block-font-12'}>{overviewData.info.top_value_raw.graded_by} {formatGrade(overviewData.info.top_value_raw.grade)}</div>
                                    </div>) : "none"}
                                    className="cursor-pointer"
                                />
                            </a>
                        </Col>
                    )}

                    <Col span={showVirtualBox ? 5 : showHighestValued ? 5 : 8}>
                        <ColorBlock
                            backgroundColor='#d23200'
                            color="white"
                            type="small"
                            leftChild={<ColorBlockIcon color="#ff3d00" icon={FlameIcon} className={!showBiggerIcons ? "whiteCircleOutlineIconTiny" : undefined} />}
                            rightChild={"" + (overviewData ? formatNumber(overviewData.info.items_hot) : 0)}
                            className="mar-bot-5"
                            onClick={() => {
                                goToCollectionWithFilters({isHot: true});
                                navigate(`/my-collection/?view=issues`);
                            }}
                        />
                        <ColorBlock
                            backgroundColor='#007aa9'
                            color="white"
                            type="small"
                            leftChild={<ColorBlockIcon color="#019dd8" icon={KeyIcon} className={!showBiggerIcons ? "whiteCircleOutlineIconTiny" : undefined} />}
                            rightChild={"" + (overviewData ? formatNumber(overviewData.info.items_key) : 0)}
                            className="mar-bot-5"
                            onClick={() => {
                                goToCollectionWithFilters({isKey: true});
                                navigate(`/my-collection/?view=issues`);
                            }}
                        />
                        <ColorBlock
                            backgroundColor='#6425b2'
                            color="white"
                            type="small"
                            leftChild={<ColorBlockIcon color="#8f37fd" icon={DiamondIcon} className={!showBiggerIcons ? "whiteCircleOutlineIconTiny" : undefined} />}
                            rightChild={"" + (overviewData ? formatNumber(overviewData.info.items_rare) : 0)}
                            className="mar-bot-5"
                            onClick={() => {
                                goToCollectionWithFilters({isRare: true});
                                navigate(`/my-collection/?view=issues`);
                            }}
                        />
                        <ColorBlock
                            backgroundColor='#2f404a'
                            color="white"
                            type="small"
                            leftChild={<ColorBlockIcon iconColor="#2f404a" color="#ffffff" icon={WishlistIcon} className={!showBiggerIcons ? "whiteCircleOutlineIconTiny" : undefined} />}
                            rightChild={"" + (overviewData ? formatNumber(overviewData.info.items_wishlist) : 0)}
                            onClick={() => {
                                navigate(`/my-collection/?view=wishlist`);
                            }}
                        />
                    </Col>
                </Row>
                {(!showOverview && !overviewQuery.isLoading) && (
                    <Row className="overviewUpgrade">
                        <Col span={24} className="messageContainer">
                            <span className="upgradeMessage">
                                You're Missing Out! <br />
                                Upgrade to See All Of your Collection Stats. <br />
                                <a href="/plans/unlimited/">Click Here</a>
                            </span>
                        </Col>
                    </Row>
                )}
                <BoxesAddDrawer visible={addBox} issue_ids={[]} series_ids={[]} onClose={() => { setAddBox(false) }} />
            </Contain>
        </Spin>
    );
};

export default MyCollectionOverview;